import { useMemo, useState, useEffect, ChangeEvent } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { TIME_24_FORMAT, API_DATE_FORMAT, DATE_TIME_FORMAT } from 'src/shared/constants';
import { formatTime, convertUserInputToTime } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

interface Props {
  incomingDateTime: string | undefined | null;
  onBlur?: (newDateTime: string | undefined) => void;
  fallbackDate?: string;
  incomingDateTimeFormat: string;
}

export const useEditingTimeInput = ({
  incomingDateTime,
  onBlur,
  fallbackDate,
  incomingDateTimeFormat = DATE_TIME_FORMAT,
}: Props) => {
  const timeFormat = useSelector(getTimeFormat);

  // Format incoming date time value to user formatted time value (e.g. 2023-04-25T09:30:00 to 9:30 am)
  const incomingTimeInputValue = useMemo(
    () =>
      incomingDateTime
        ? formatTime(moment(incomingDateTime, incomingDateTimeFormat).format(TIME_24_FORMAT), timeFormat)
        : '',
    [incomingDateTime, timeFormat],
  );

  // Editing state to be used for text input
  const [editingTimeInputValue, setEditingTimeInputValue] = useState(incomingTimeInputValue);

  // Reset editing state whenever incomingDateTime changes
  useEffect(() => {
    setEditingTimeInputValue(incomingTimeInputValue);
  }, [incomingTimeInputValue]);

  // Update editing state
  const handleEditingTimeInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEditingTimeInputValue(event.target.value);
  };

  // Convert user input state to formatted time and pass to onBlur callback function
  const handleEditingTimeInputBlur = () => {
    // Allow time input to be cleared (undefined) ONLY if a fallback date is provided
    if (!editingTimeInputValue && fallbackDate) {
      return onBlur?.(undefined);
    }

    const convertedTime = convertUserInputToTime(editingTimeInputValue);

    // Revert if input value is invalid
    if (!convertedTime) {
      return setEditingTimeInputValue(incomingTimeInputValue);
    }

    const date = incomingDateTime ? moment(incomingDateTime).format(API_DATE_FORMAT) : fallbackDate?.split('T')[0];
    const newDateTime = `${date}T${convertedTime}`;

    // Trigger state change if new value is the same as previous value  (needed to trigger formatting useMemo)
    if (newDateTime === incomingDateTime) {
      return setEditingTimeInputValue(incomingTimeInputValue);
    }
    onBlur?.(newDateTime);
  };

  return {
    value: editingTimeInputValue,
    onInputChange: handleEditingTimeInputValueChange,
    onInputBlur: handleEditingTimeInputBlur,
  };
};
